import React from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {labels} from '../../../../../../../util/utils';
import {controlType, entities} from '../../../../../../../util/uiconstants';
import Utility from '../../../../../../../util/utilityfunctions';
import {applyFilters} from '../Common/clientFilters.utils';
import BodySpecialCardItem from '../BodySpecialCardItem';
import BodySpecialAccordionHeaderColumn from '../BodySpecialAccordionHeaderColumn';
import ControlCountLabel from '../ControlCountLabel';
import ScotStrategy from '../ScotStrategy';
import ScotItAppCount from '../ScotItAppCount';
import ISA315ScotItAppCardItemContent from './ISA315ScotItAppCardItemContent';
import {
	getBodyMetadataSlice,
	getScotControls,
	getControlsAccumulator
} from '../../../../../../../selectors/ISA315/selectors';
import BodySpecialListItemNoDataPlaceholder from '../BodySpecialListItemNoDataPlaceholder';

function ISA315ScotItApplicationBodyListItem({
	scotId,
	bodyId,
	className,
	isTargetEngagement,
	onDropdownOptionClick,
	onViewControlClick
}) {
	const itApplicationsSlice = useSelector((state) => state.bodiesSpecial.itApplications);
	const controlsSlice = useSelector((state) => state.bodiesSpecial.controls);

	const scot = useSelector((state) => state.bodiesSpecial.scots.byId[scotId]);

	const bodyMetadata = useSelector((state) => getBodyMetadataSlice(state, bodyId));

	const controls = useSelector((state) => getScotControls(state, scot.id));

	let scotItApplications = applyFilters(
		entities.ItSoApplication,
		bodyMetadata.clientFilters,
		scot.itApplications,
		itApplicationsSlice.byId
	);

	const accumulator = useSelector((state) => getControlsAccumulator(state, scot, itApplicationsSlice, controlsSlice));

	const dropdownOptions = [
		{
			render: <div>{!isTargetEngagement ? labels.editScotsISA : labels.viewScot}</div>,
			onClick: () => {
				openEditScot();
			}
		},
		{
			render: <div>{labels.viewControlISA}</div>,
			isVisible: controls.length > 0,
			onClick: () => {
				onViewControlClick({
					entityId: entities.Control,
					scotName: scot.scotName,
					scot: {...scot, itApplications: scotItApplications}
				});
			}
		}
	];

	const headerConfig = [
		{
			render: <StyledScotColumn header={labels.scotHeader} value={scot.scotName} className={'ScotColumnHeader'} />
		},
		{
			render: (
				<StyledStrategyColumn
					header={labels.strategyTitle}
					value={<ScotStrategy strategyTypeId={scot.strategyTypeId} />}
					className={'StrategyColumnHeader'}
				/>
			)
		},
		{
			render: (
				<StyledScotRelatedItAppColumn
					header={labels.scotRelatedItApp}
					value={<ScotItAppCount scot={scot} itApplications={itApplicationsSlice} />}
					className={'RelatedItAppColumnHeader'}
				/>
			)
		},
		{
			header: labels.controlsHeader,
			className: 'StyledControlsHeader primary-entity__child-entity',
			render: [
				{
					render: (
						<StyledSubGroupColumnLeft
							header={labels.relatedItApps}
							value={Utility.showISA315Counter(accumulator.count)}
							className={'RelatedItApps'}
						/>
					)
				},
				{
					render: (
						<StyledSubGroupColumnLeft
							header={labels.controlsApplicationHeader}
							value={
								accumulator.controlsWithNoItApps[controlType.itApplicationControl].length > 0 ? (
									<StyledErrorCount
										controlIds={scot.controls}
										controlTypeId={controlType.itApplicationControl}
										controlsSlice={controlsSlice}
									/>
								) : (
									<ControlCountLabel
										controlIds={scot.controls}
										controlTypeId={controlType.itApplicationControl}
										controlsSlice={controlsSlice}
									/>
								)
							}
							className={'ControlsApplicationHeader'}
						/>
					)
				},
				{
					render: (
						<StyledSubGroupColumnRight
							header={labels.controlsITDMHeader}
							value={
								accumulator.controlsWithNoItApps[controlType.itDependentManualControl].length > 0 ? (
									<StyledErrorCount
										controlIds={scot.controls}
										controlTypeId={controlType.itDependentManualControl}
										controlsSlice={controlsSlice}
									/>
								) : (
									<ControlCountLabel
										controlIds={scot.controls}
										controlTypeId={controlType.itDependentManualControl}
										controlsSlice={controlsSlice}
									/>
								)
							}
							className={'ControlsITDMHeader'}
						/>
					)
				}
			]
		}
	];

	const openEditScot = () => {
		onDropdownOptionClick(scotId);
	};

	const placeHoldersConfig = [
		{
			show: !scot.hasNoITApplication && scotItApplications.length === 0,
			text: labels.addOrRelateItAppPlaceholder,
			config: {
				identifyRelatedItApps: openEditScot,
				documentScotHasNoItApps: openEditScot
			}
		},
		{
			show: scot.hasNoITApplication && scot.controls.length > 0,
			text: labels.correctScotDocumentationPlaceholder,
			config: {
				correctScotDocumentation: openEditScot
			}
		}
	];

	//Placeholder must ALWAYS be calculated before concating related it apps. Otherwise they will count as scot's it apps.
	const placeholder = placeHoldersConfig.find((_placeholder) => _placeholder.show);

	scotItApplications = scotItApplications.concat(accumulator.relatedItApps);

	const disabledScot =
		placeholder !== undefined ||
		(scot.hasNoITApplication && accumulator.controlsWithNoItApps.all.length === 0 && scotItApplications.length === 0);

	return (
		<StyledISA315ScotItApplicationBodyListItem className={`StyledISA315ScotItApplicationBodyListItem ${className}`}>
			<BodySpecialCardItem
				accordion
				config={headerConfig}
				dropdownOptions={dropdownOptions}
				disabled={disabledScot}
				className={placeholder ? 'noChildrenAvailable' : ''}
			>
				{!placeholder && !disabledScot && (
					<ISA315ScotItAppCardItemContent
						allItAppsIds={scotItApplications}
						applicationControlIds={accumulator.controlsWithNoItApps[controlType.itApplicationControl]}
						itdmControlIds={accumulator.controlsWithNoItApps[controlType.itDependentManualControl]}
						scotId={scotId}
					/>
				)}
			</BodySpecialCardItem>
			{placeholder && (
				<BodySpecialListItemNoDataPlaceholder
					placeholderText={placeholder.text}
					onPlaceholderClickConfig={placeholder.config}
				/>
			)}
		</StyledISA315ScotItApplicationBodyListItem>
	);
}

ISA315ScotItApplicationBodyListItem.propTypes = {
	scotId: PropTypes.number.isRequired,
	className: PropTypes.string,
	onDropdownOptionClick: PropTypes.func,
	onViewControlClick: PropTypes.func
};

ISA315ScotItApplicationBodyListItem.defaultProps = {
	className: 'isa315-scot-it-application-body-list-item'
};

export default ISA315ScotItApplicationBodyListItem;

const StyledISA315ScotItApplicationBodyListItem = styled.section`
	margin-bottom: var(--px-20);
	&:last-child {
		margin-bottom: var(--px-10);
	}
	&:first-child {
		.motif-accordion {
			margin-top: 0 !important;
		}
	}
	.StyledControlsHeader {
		&.primary-entity__child-entity {
			width: var(--px-360);
			font-size: var(--px-12) !important;
			font-weight: 300 !important;
		}
	}
	.StyledControlsWithNoItApps {
		.StyledCountColumnApplication {
			&.StyledCountValue {
				padding-left: var(--px-6);
			}
		}
		.StyledCountColumnITDM {
			&.StyledCountValue {
				padding-left: var(--px-26);
			}
		}
	}
`;

const StyledScotColumn = styled(BodySpecialAccordionHeaderColumn)`
	width: calc(100% - var(--px-700));
	overflow: hidden;
`;

const StyledStrategyColumn = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-160);
	padding: var(--px-30) var(--px-14) var(--px-18) var(--px-14) !important;
	height: var(--px-87);
	line-height: var(--px-24);
`;

const StyledScotRelatedItAppColumn = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-160);
	padding: var(--px-30) var(--px-14) var(--px-18) var(--px-14) !important;
	height: var(--px-87);
	line-height: var(--px-24);
`;

const StyledSubGroupColumnLeft = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-120);
	padding: 0 var(--px-10) 0 0 !important;
`;

const StyledSubGroupColumnRight = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-90);
	padding: 0 0 0 var(--px-10) !important;
`;

const StyledErrorCount = styled(ControlCountLabel)`
	color: var(--red-600) !important;
	.control-count-label {
		color: var(--red-600) !important;
	}
`;
